// src/LeadPage.jsx
// Entry gate: visitors leave their details once before reaching the tour and
// master plan. Leads land in the Firestore `leads` collection.

import React, { useEffect, useState } from "react";
import "./LeadPage.css";

import { COMPANY_LOGO as LeadLogo } from "./brandAssets";

const PROJECT_NAME = "The Aurora Hills";
const DEVELOPER = "The House of Auroras Presents";
// Behind the sheet and on its photo side: a still of the tour's Aerial View,
// facing NH-48 with the site's plots beside it - the same image as the home page.
const BACKGROUND_IMAGE = "/assets/aurora-hills-aerial.webp";

// Project highlights on the photo side.
const HIGHLIGHTS = [
  "30 Acres – Premium Plotted Development",
  "389 Premium Plots",
  "190 Exclusive Plots Available",
  "A Well-Developed Gated Community",
  "HDUDA & NA-KJP Approved",
];

// Firestore is only needed on submit, so it stays out of the first-paint bundle
// and is fetched in the background once the form is on screen.
let firestorePromise = null;
const loadFirestore = () => {
  if (!firestorePromise) {
    firestorePromise = Promise.all([import("firebase/firestore"), import("./firebase")]).catch((err) => {
      firestorePromise = null;
      throw err;
    });
  }
  return firestorePromise;
};

// A submitted lead goes straight into the 360° tour, so its code chunk and
// first panorama are fetched while the visitor is still filling in the form.
// Dynamic imports keep three.js out of this page's own bundle; the chunk is
// the same one App.jsx lazy-loads for /explore.
let tourWarmed = false;
const warmTour = () => {
  if (tourWarmed) return;
  tourWarmed = true;
  import("./Eartho/Tour").catch(() => {});
  Promise.all([import("./pano/tileCache"), import("./Eartho/tourData")])
    .then(([{ preloadCube }, { AERIAL_BASE_PATH }]) => preloadCube({ basePath: AERIAL_BASE_PATH, level: 1 }))
    .catch(() => {
      tourWarmed = false;
    });
};

export const LEAD_SUBMITTED_KEY = "auroraHillsLeadSubmitted";

export function hasSubmittedLead() {
  try {
    return localStorage.getItem(LEAD_SUBMITTED_KEY) === "true";
  } catch {
    return false;
  }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// India first, then the Gulf (where most NRI enquiries come from), then A-Z.
// `digits` is the accepted length of the number after the country code; each
// code appears once because it doubles as the <option> value.
const COUNTRY_CODES = [
  { code: "+91", label: "India", digits: [10, 10] },

  { code: "+971", label: "UAE", digits: [9, 9] },
  { code: "+966", label: "Saudi Arabia", digits: [9, 9] },
  { code: "+974", label: "Qatar", digits: [8, 8] },
  { code: "+965", label: "Kuwait", digits: [8, 8] },
  { code: "+968", label: "Oman", digits: [8, 8] },
  { code: "+973", label: "Bahrain", digits: [8, 8] },

  { code: "+93", label: "Afghanistan", digits: [9, 9] },
  { code: "+54", label: "Argentina", digits: [10, 11] },
  { code: "+61", label: "Australia", digits: [9, 9] },
  { code: "+43", label: "Austria", digits: [10, 13] },
  { code: "+880", label: "Bangladesh", digits: [10, 10] },
  { code: "+32", label: "Belgium", digits: [9, 9] },
  { code: "+975", label: "Bhutan", digits: [8, 8] },
  { code: "+55", label: "Brazil", digits: [10, 11] },
  { code: "+673", label: "Brunei", digits: [7, 7] },
  { code: "+855", label: "Cambodia", digits: [8, 9] },
  { code: "+86", label: "China", digits: [11, 11] },
  { code: "+420", label: "Czech Republic", digits: [9, 9] },
  { code: "+45", label: "Denmark", digits: [8, 8] },
  { code: "+20", label: "Egypt", digits: [10, 10] },
  { code: "+358", label: "Finland", digits: [9, 10] },
  { code: "+33", label: "France", digits: [9, 9] },
  { code: "+49", label: "Germany", digits: [10, 11] },
  { code: "+233", label: "Ghana", digits: [9, 9] },
  { code: "+30", label: "Greece", digits: [10, 10] },
  { code: "+852", label: "Hong Kong", digits: [8, 8] },
  { code: "+36", label: "Hungary", digits: [9, 9] },
  { code: "+62", label: "Indonesia", digits: [9, 12] },
  { code: "+98", label: "Iran", digits: [10, 10] },
  { code: "+964", label: "Iraq", digits: [10, 10] },
  { code: "+353", label: "Ireland", digits: [9, 9] },
  { code: "+972", label: "Israel", digits: [9, 9] },
  { code: "+39", label: "Italy", digits: [9, 10] },
  { code: "+81", label: "Japan", digits: [10, 10] },
  { code: "+962", label: "Jordan", digits: [9, 9] },
  { code: "+7", label: "Kazakhstan / Russia", digits: [10, 10] },
  { code: "+254", label: "Kenya", digits: [9, 9] },
  { code: "+961", label: "Lebanon", digits: [7, 8] },
  { code: "+60", label: "Malaysia", digits: [9, 10] },
  { code: "+960", label: "Maldives", digits: [7, 7] },
  { code: "+230", label: "Mauritius", digits: [8, 8] },
  { code: "+52", label: "Mexico", digits: [10, 10] },
  { code: "+212", label: "Morocco", digits: [9, 9] },
  { code: "+95", label: "Myanmar", digits: [8, 10] },
  { code: "+977", label: "Nepal", digits: [10, 10] },
  { code: "+31", label: "Netherlands", digits: [9, 9] },
  { code: "+64", label: "New Zealand", digits: [8, 10] },
  { code: "+234", label: "Nigeria", digits: [10, 10] },
  { code: "+47", label: "Norway", digits: [8, 8] },
  { code: "+92", label: "Pakistan", digits: [10, 10] },
  { code: "+63", label: "Philippines", digits: [10, 10] },
  { code: "+48", label: "Poland", digits: [9, 9] },
  { code: "+351", label: "Portugal", digits: [9, 9] },
  { code: "+40", label: "Romania", digits: [9, 9] },
  { code: "+65", label: "Singapore", digits: [8, 8] },
  { code: "+27", label: "South Africa", digits: [9, 9] },
  { code: "+82", label: "South Korea", digits: [9, 10] },
  { code: "+34", label: "Spain", digits: [9, 9] },
  { code: "+94", label: "Sri Lanka", digits: [9, 9] },
  { code: "+46", label: "Sweden", digits: [7, 9] },
  { code: "+41", label: "Switzerland", digits: [9, 9] },
  { code: "+886", label: "Taiwan", digits: [9, 9] },
  { code: "+255", label: "Tanzania", digits: [9, 9] },
  { code: "+66", label: "Thailand", digits: [9, 9] },
  { code: "+90", label: "Turkey", digits: [10, 10] },
  { code: "+256", label: "Uganda", digits: [9, 9] },
  { code: "+380", label: "Ukraine", digits: [9, 9] },
  { code: "+44", label: "United Kingdom", digits: [10, 10] },
  { code: "+1", label: "USA / Canada", digits: [10, 10] },
  { code: "+84", label: "Vietnam", digits: [9, 10] },
  { code: "+967", label: "Yemen", digits: [9, 9] },
  { code: "+260", label: "Zambia", digits: [9, 9] },
  { code: "+263", label: "Zimbabwe", digits: [9, 9] },
];

export default function LeadPage({ onSuccess }) {
  const [name, setName] = useState("");
  const [countryCode, setCountryCode] = useState(COUNTRY_CODES[0].code);
  const [mobileNumber, setMobileNumber] = useState("");
  const [email, setEmail] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [submitted, setSubmitted] = useState(false);
  const [countdown, setCountdown] = useState(3);

  const validate = () => {
    if (!name.trim()) return "Please enter your name.";

    const m = mobileNumber.replace(/\D/g, "");
    const [min, max] = (COUNTRY_CODES.find((c) => c.code === countryCode) || {}).digits || [6, 14];
    if (m.length < min || m.length > max) {
      return min === max
        ? `Please enter a ${min}-digit mobile number.`
        : "Please enter a valid mobile number.";
    }

    if (!EMAIL_RE.test(email.trim())) return "Please enter a valid email.";

    if (!agreed) return "Please agree to be contacted to continue.";

    return "";
  };

  useEffect(() => {
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
    idle(() => {
      loadFirestore().catch(() => {});
      warmTour();
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setError("");

    const msg = validate();
    if (msg) return setError(msg);

    setSaving(true);
    warmTour();

    try {
      const [{ addDoc, collection, serverTimestamp }, { db }] = await loadFirestore();
      // Field list must match isValidLead() in firestore.rules.
      await addDoc(collection(db, "leads"), {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        // Full international number, e.g. "+919876543210".
        mobileNumber: `${countryCode}${mobileNumber.replace(/\D/g, "")}`,
        countryCode,
        projectName: PROJECT_NAME,
        message: "Virtual tour viewed",
        source: "Virtual Tour",
        status: "New",
        notes: "",
        timestamp: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastUpdate: serverTimestamp(),
      });

      try {
        localStorage.setItem(LEAD_SUBMITTED_KEY, "true");
      } catch {
        // Private mode / blocked storage: they'll just see the form next visit.
      }

      setSubmitted(true);
      setCountdown(3);
    } catch (err) {
      console.error("Error saving lead:", err);
      setError("Couldn't save your details. Please check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!submitted) return;

    if (countdown <= 0) {
      onSuccess?.();
      return;
    }

    const timer = setTimeout(() => setCountdown((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [submitted, countdown, onSuccess]);

  return (
    // The backdrop is passed as a variable: an absolute URL in the stylesheet
    // would make the bundler try to resolve it from src/.
    <div className="lead-shell" style={{ "--lead-backdrop": `url("${BACKGROUND_IMAGE}")` }}>

      <section
        className="lead-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-heading"
      >
        <div className="lead-grip" aria-hidden="true" />

        {/* Photo side: the same aerial still as the backdrop, under a dark fade. */}
        <div className="lead-cover">
          <img className="lead-cover-photo" src={BACKGROUND_IMAGE} alt="" />
          <img className="lead-cover-logo" src={LeadLogo} alt="The House of Auroras" width={120} height={80} />
          <div className="lead-cover-copy">
            <span className="lead-eyebrow company-name">{DEVELOPER}</span>
            <h1>
              The Aurora <strong>Hills</strong>
            </h1>
            <p className="lead-tagline">Where nature meets the art of living</p>
            <p className="lead-location">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" />
                <circle cx="12" cy="9.5" r="2.5" />
              </svg>
              Adjacent to Karnatak University, Sunset View Point, Dharwad
            </p>
            <ul className="lead-highlights">
              {HIGHLIGHTS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="lead-panel">
          {!submitted ? (
            <>
              <span className="lead-eyebrow">Welcome</span>
              <h2 id="lead-heading" className="lead-title">
                Unlock the <strong>experience.</strong>
              </h2>
              <p className="lead-subtitle">
                Share a few details to walk the site in 360° and see live plot
                availability, dimensions and pricing.
              </p>

              <form onSubmit={handleSubmit} className="lead-form" noValidate>
                <label className="lead-field">
                  <span className="lead-label">Full name</span>
                  <input
                    id="lead-name"
                    className="lead-input"
                    placeholder="Your name"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </label>

                <div className="lead-field">
                  <label className="lead-label" htmlFor="lead-mobile">Mobile number</label>
                  <div className="lead-phone">
                    <select
                      className="lead-input lead-country"
                      aria-label="Country code"
                      autoComplete="tel-country-code"
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                    >
                      {COUNTRY_CODES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.code} {c.label}
                        </option>
                      ))}
                    </select>
                    <input
                      id="lead-mobile"
                      className="lead-input"
                      type="tel"
                      placeholder="Mobile number"
                      autoComplete="tel-national"
                      inputMode="numeric"
                      value={mobileNumber}
                      onChange={(e) => setMobileNumber(e.target.value)}
                    />
                  </div>
                </div>

                <label className="lead-field">
                  <span className="lead-label">Email address</span>
                  <input
                    id="lead-email"
                    className="lead-input"
                    type="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </label>

                <label className="lead-agree">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => {
                      setAgreed(e.target.checked);
                      if (e.target.checked && error) setError("");
                    }}
                  />
                  <span className="lead-agree-box" aria-hidden="true" />
                  <span>
                    I agree to be contacted by <b>The House of Auroras</b> about{" "}
                    {PROJECT_NAME} by call, WhatsApp, SMS or email.
                  </span>
                </label>

                {error && (
                  <div className="lead-error" role="alert">
                    {error}
                  </div>
                )}

                <button className="lead-action" disabled={saving} type="submit">
                  {saving ? "Preparing your experience…" : "Start exploring"}
                  {/* Drawn as SVG: the ↗ character renders as an emoji on iOS. */}
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                </button>
              </form>
            </>
          ) : (
            <div className="lead-success" role="status">
              <span className="lead-eyebrow">Access approved</span>
              <h2 className="lead-title">
                You&apos;re <strong>all set.</strong>
              </h2>
              <p className="lead-subtitle">
                Walk the site in 360°, discover available plots, compare
                dimensions and view pricing on the master plan.
              </p>

              <ul className="lead-points">
                <li><small>01</small>360° virtual tour</li>
                <li><small>02</small>Live plot availability</li>
                <li><small>03</small>Plot pricing</li>
              </ul>

              <div className="lead-countdown">
                <span>{countdown}</span>
                Taking you in…
              </div>
            </div>
          )}

          <p className="lead-powered">
            Powered by{" "}
            <a href="https://www.luminexa.in" target="_blank" rel="noopener noreferrer">
              Luminexa Technologies Pvt Ltd
            </a>
          </p>
        </div>
      </section>
    </div>
  );
}
